import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { accountApi } from "../api/accountApi";
import Message from "../components/Message";

export default function AccountSettings() {
  const [activeTab, setActiveTab] = useState("profile");
  const [user, setUser] = useState(null);
  const [addressForm, setAddressForm] = useState({ address: "", city: "", zip_code: "" });
  const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "", confirm_new_password: "" });
  const [notice, setNotice] = useState(null);
  const [accessLogs, setAccessLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState({ startDate: "", endDate: "" });

  useEffect(() => {
    accountApi.getAccount().then((data) => {
      const currentUser = data.user || data;
      setUser(currentUser);
      setAddressForm({
        address: currentUser.address || "",
        city: currentUser.city || "",
        zip_code: currentUser.zip_code || "",
      });
    }).catch((error) => setNotice({ type: "error", text: error.message }));
  }, []);

  useEffect(() => {
    if(activeTab === "access-logs") fetchAccessLogs();
  }, [activeTab]);

  async function fetchAccessLogs() {
    setLogsLoading(true);
    try {
      const data = await accountApi.getAccessLogs({
        startDate: dateFilter.startDate || undefined,
        endDate: dateFilter.endDate || undefined,
      });
      setAccessLogs(data.logs || []);
      } catch (error) {
        setNotice({ type: "error", text: error.message });
      } finally {
        setLogsLoading(false);
      }
    }
    function handleDateSearch(e) {
      e.preventDefault();
      fetchAccessLogs();
    }
    function clearDateFilter() {
      setDateFilter({ startDate: "", endDate: "" });
      accountApi.getAccessLogs().then((data) => setAccessLogs(data.logs || []));
    }
  

  async function saveAddress(event) {
    event.preventDefault();
    try {
      await accountApi.updateAddress(addressForm);
      setNotice({ type: "success", text: "Address updated successfully!" });
    } catch (error) {
      setNotice({ type: "error", text: error.message });
    }
  }

  async function updatePassword(event) {
    event.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_new_password) {
      setNotice({ type: "error", text: "Passwords do not match!" });
      return;
    }

    try {
      await accountApi.updatePassword({
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      setPasswordForm({ current_password: "", new_password: "", confirm_new_password: "" });
      setNotice({ type: "success", text: "Password updated successfully!" });
    } catch (error) {
      setNotice({ type: "error", text: error.message });
    }
  }

  if (!user) {
    return <div className="container"><Message notice={notice || { type: "info", text: "Loading account..." }} /></div>;
  }

  return (
    <div className="account-container">
      <div className="container">
        <h1 className="page-title">Account Settings</h1>
        <Message notice={notice} />

        <div className="account-tabs">
          <button className={`tab-button ${activeTab === "profile" ? "active" : ""}`} onClick={() => setActiveTab("profile")}>Profile</button>
          <button className={`tab-button ${activeTab === "address" ? "active" : ""}`} onClick={() => setActiveTab("address")}>Delivery Address</button>
          <button className={`tab-button ${activeTab === "password" ? "active" : ""}`} onClick={() => setActiveTab("password")}>Change Password</button>
          <button className={`tab-button ${activeTab === "access-logs" ? "active" : ""}`} onClick={() => setActiveTab("access-logs")}>Access Logs</button>
        </div>

        <div className="tab-content">
          {activeTab === "profile" && (
            <div className="tab-pane active">
              <div className="form-section">
                <h2>Profile Information</h2>
                <div className="info-row"><label>Email:</label><span>{user.email}</span></div>
                <div className="info-row"><label>First Name:</label><span>{user.first_name}</span></div>
                <div className="info-row"><label>Last Name:</label><span>{user.last_name}</span></div>
                <div className="info-row"><label>Member Since:</label><span>{new Date(user.created_at).toLocaleDateString()}</span></div>
              </div>
            </div>
          )}

          {activeTab === "address" && (
            <div className="tab-pane active">
              <div className="form-section">
                <h2>Delivery Address</h2>
                <form onSubmit={saveAddress}>
                  <div className="form-group">
                    <label htmlFor="address">Street Address</label>
                    <input type="text" id="address" className="form-control" value={addressForm.address} onChange={(e) => setAddressForm({ ...addressForm, address: e.target.value })} />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="city">City</label>
                      <input type="text" id="city" className="form-control" value={addressForm.city} onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="zip_code">ZIP Code</label>
                      <input type="text" id="zip_code" className="form-control" value={addressForm.zip_code} onChange={(e) => setAddressForm({ ...addressForm, zip_code: e.target.value })} />
                    </div>
                  </div>
                  <button type="submit" className="btn btn-primary">Save Address</button>
                </form>
              </div>
            </div>
          )}

          {activeTab === "password" && (
            <div className="tab-pane active">
              <div className="form-section">
                <h2>Change Password</h2>
                <form onSubmit={updatePassword}>
                  <div className="form-group">
                    <label htmlFor="current_password">Current Password</label>
                    <input type="password" id="current_password" className="form-control" required value={passwordForm.current_password} onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="new_password">New Password</label>
                    <input type="password" id="new_password" className="form-control" required value={passwordForm.new_password} onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })} />
                    <small className="password-help">Minimum 8 characters, must contain uppercase letter and number</small>
                  </div>
                  <div className="form-group">
                    <label htmlFor="confirm_new_password">Confirm New Password</label>
                    <input type="password" id="confirm_new_password" className="form-control" required value={passwordForm.confirm_new_password} onChange={(e) => setPasswordForm({ ...passwordForm, confirm_new_password: e.target.value })} />
                  </div>
                  <button type="submit" className="btn btn-primary">Update Password</button>
                </form>
              </div>
            </div>
          )}

          {activeTab === "access-logs" && (
            <div className="tab-pane active">
              <div className="form-section">
                <h2>Access Logs</h2>
                <form onSubmit={handleDateSearch}>
                  <input type="date" value={dateFilter.startDate}
                    onChange={(e) => setDateFilter({...dateFilter, startDate: e.target.value})} />
                    <input type="date" value={dateFilter.endDate}
                    onChange={(e) => setDateFilter({...dateFilter, endDate: e.target.value})} />
                    <button type="submit">Search</button>
                </form>
                {logsLoading ? <p>Loading...</p> : (
                  <table>
                    {accessLogs.map((log) => (
                      <tr key={log.id}>
                        <td>{log.action}</td>
                        <td>{new Date(log.timestamp).toLocaleString()}</td>
                        <td>{log.user_agent || "-"}</td>
                      </tr>
                    ))}
                  </table>
                )}
              </div>
            </div>
          )}

        </div>

        <Link to="/my-orders" className="btn btn-secondary" style={{ marginTop: 20 }}>Back to Orders</Link>
      </div>
    </div>
  );
}
